import { observer } from 'mobx-react-lite'
import { Alert, Button, Label, TextareaWithLabel, UploadDoc, Warning } from 'shared/ui'
import { UploadedFileCard } from 'entities/Attachment'
import { PageDescription } from 'pages/settings/ui/PageDescription/PageDescription'
import { NextButton } from 'pages/settings/ui/NextButton/NextButton'
import { useRcsWizardContext } from 'pages/settings/pages/rcsV2/context/rcsWizardContext'
import { RcsWizardSteps } from 'pages/settings/pages/rcsV2/route/type'
import { openRcsExampleModal } from 'pages/settings/pages/rcsV2/ui'

import styles from './styles.module.scss'

export const OptInMessageInfo = observer(() => {
  const {
    nextStepClick,
    saving,
    isUsFlow,
    optInMessageInfoStore: {
      messageFlow,
      messageFlowError,
      optInImage,
      optInImageUrl,
      optInImageError,
      optInImageLoading,
      optOut,
      optOutError,
      messageFlowMinLength,
      messageFlowMaxLength,
      optOutMinLength,
      optOutMaxLength,
      sampleMessage,
      sampleMessageError,
      sampleMessagePlaceholder,
      sampleOptOut,
      sampleOptOutError,
      sampleOptOutPlaceholder,
      organizationName,
      sampleMessageMaxLength,
      sampleMessageSegmentWarning,
      sampleOptOutSegmentWarning,
      isNextDisabled,
      setMessageFlow,
      setOptInImage,
      removeOptInImage,
      clearOptInImageError,
      setOptOut,
      setSampleMessage,
      setSampleOptOut,
      openOptInImageExample,
    },
  } = useRcsWizardContext()

  const onNext = () => nextStepClick(RcsWizardSteps.optInMessageInfo)

  return (
    <div className={styles.wrap}>
      <PageDescription>
        Provide examples of how users opt in, receive messages, and unsubscribe from your RCS
        campaigns.
      </PageDescription>

      <TextareaWithLabel
        className={styles.messageFlowField}
        classNameTextarea={styles.fieldText}
        value={messageFlow}
        onChange={setMessageFlow}
        error={messageFlowError}
        limit={messageFlowMaxLength}
        minLimit={messageFlowMinLength}
        errorType='bottomText'
        name='description'
        placeholder='Describe how users discover your business and give consent to receive messages, such as filling out a form or checking an opt-in box on your website'
        labelProps={{
          marginBottom: 0,
          label: {
            text: 'Message flow and opt-in description',
            rightNode: (
              <Button
                onClick={() => openRcsExampleModal('messageFlow')}
                text='See example'
                size='small'
                typeBtn='informative'
              />
            ),
          },
        }}
      />

      <Label
        marginTop={24}
        marginBottom={4}
        questionTooltipProps={{
          type: 'description',
          label:
            'Provide an image of the opt-in process where the customer entered their phone number and agreed to receive SMS. Ensure it meets the above criteria.',
        }}
        label={{
          text: 'Opt-in policy image',
          rightNode: (
            <Button
              onClick={openOptInImageExample}
              text='See example'
              size='small'
              typeBtn='informative'
            />
          ),
        }}
      />

      {optInImageError && (
        <Alert
          item={{ title: optInImageError.title, desc: optInImageError.desc, type: 'error' }}
          onClose={clearOptInImageError}
          marginBottom={12}
        />
      )}

      <UploadDoc uploadTypeText='Acceptable file types: jpg, png' onChange={setOptInImage} />

      {(optInImage || optInImageUrl) && (
        <UploadedFileCard
          file={optInImage ?? undefined}
          url={optInImageUrl}
          type='image'
          loading={optInImageLoading}
          onRemove={removeOptInImage}
        />
      )}

      <TextareaWithLabel
        className={styles.optOutField}
        classNameTextarea={styles.fieldText}
        value={optOut}
        onChange={setOptOut}
        error={optOutError}
        limit={optOutMaxLength}
        minLimit={optOutMinLength}
        errorType='bottomText'
        name='description'
        placeholder='Explain how users can unsubscribe or stop receiving messages'
        labelProps={{
          marginBottom: 0,
          label: {
            text: 'Opt-out description',
            rightNode: (
              <Button
                onClick={() => openRcsExampleModal('optOut')}
                text='See example'
                size='small'
                typeBtn='informative'
              />
            ),
          },
        }}
      />

      {isUsFlow && (
        <>
          <TextareaWithLabel
            className={styles.sampleField}
            classNameTextarea={styles.fieldText}
            value={sampleMessage}
            onChange={setSampleMessage}
            error={sampleMessageError}
            limit={sampleMessageMaxLength}
            errorType='bottomText'
            name='sampleMessage'
            placeholder={sampleMessagePlaceholder}
            labelProps={{
              marginBottom: 0,
              label: {
                text: 'Sample help message',
                rightNode: (
                  <Button
                    onClick={() => openRcsExampleModal('sampleHelp', organizationName)}
                    text='See example'
                    size='small'
                    typeBtn='informative'
                  />
                ),
              },
            }}
            bottomNode={
              sampleMessageSegmentWarning
                ? { leftNode: <Warning text={sampleMessageSegmentWarning} /> }
                : undefined
            }
          />

          <TextareaWithLabel
            className={styles.sampleField}
            classNameTextarea={styles.fieldText}
            value={sampleOptOut}
            onChange={setSampleOptOut}
            error={sampleOptOutError}
            limit={sampleMessageMaxLength}
            errorType='bottomText'
            name='sampleOptOut'
            placeholder={sampleOptOutPlaceholder}
            labelProps={{
              marginBottom: 0,
              label: {
                text: 'Sample opt-out message',
                rightNode: (
                  <Button
                    onClick={() => openRcsExampleModal('sampleOptOut', organizationName)}
                    text='See example'
                    size='small'
                    typeBtn='informative'
                  />
                ),
              },
            }}
            bottomNode={
              sampleOptOutSegmentWarning
                ? { leftNode: <Warning text={sampleOptOutSegmentWarning} /> }
                : undefined
            }
          />
        </>
      )}

      <NextButton
        text='Next'
        disabled={isNextDisabled || saving}
        loading={saving}
        onClick={onNext}
      />
    </div>
  )
})
